import React, { useState, useEffect } from 'react';
import { Bell, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { QuietHoursTimePicker } from '../../components/admin/QuietHoursTimePicker';
import { NotificationDefaults, NotificationDefaultsUpdate } from '../../types/system-settings';
import { getNotificationDefaults, updateNotificationDefaults } from '../../api/system-settings';

/**
 * NotificationAdminPage - Configure default notification preferences
 * 
 * Feature: System Administration Interface (001-admin-settings)
 * User Story: US5 - Configure Notification Preferences (Priority P3)
 * 
 * Features:
 * - Enable/disable email notifications by default for new users
 * - Set default notification frequency (immediate, daily, weekly)
 * - Configure which event types trigger notifications
 * - Set quiet hours (no notifications during specified time range)
 * - Admin-only access with immediate effect on save
 */
const NotificationAdminPage: React.FC = () => {
    const [defaults, setDefaults] = useState<NotificationDefaults | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Form state
    const [emailEnabled, setEmailEnabled] = useState(true);
    const [frequency, setFrequency] = useState<'immediate' | 'daily' | 'weekly'>('immediate');
    const [eventTypes, setEventTypes] = useState<string[]>([]);
    const [quietHoursStart, setQuietHoursStart] = useState('');
    const [quietHoursEnd, setQuietHoursEnd] = useState('');
    const [useQuietHours, setUseQuietHours] = useState(false);

    // Available event types with descriptions
    const availableEventTypes = [
        {
            value: 'task_assigned',
            label: 'Task Assigned',
            description: 'Notify when a task is assigned to a user'
        },
        {
            value: 'task_due_soon',
            label: 'Task Due Soon',
            description: 'Notify when a task due date is approaching (24 hours before)'
        },
        {
            value: 'task_overdue',
            label: 'Task Overdue',
            description: 'Notify when a task becomes overdue'
        },
        {
            value: 'project_update',
            label: 'Project Update',
            description: 'Notify when a project affecting the user is updated'
        },
        {
            value: 'mention',
            label: 'Mention',
            description: 'Notify when user is mentioned in comments'
        },
        {
            value: 'weekly_summary',
            label: 'Weekly Summary',
            description: 'Send weekly summary email on Monday morning'
        }
    ];

    // Helper function to normalize time to HH:MM format (strip seconds if present)
    const normalizeTime = (time: string | null): string => {
        if (!time) return '';
        // If time has seconds (HH:MM:SS), strip them to get HH:MM
        const parts = time.split(':');
        if (parts.length >= 2) {
            return `${parts[0]}:${parts[1]}`;
        }
        return time;
    };

    useEffect(() => {
        loadDefaults();
    }, []);

    const loadDefaults = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await getNotificationDefaults();
            setDefaults(data);
            
            // Populate form fields
            setEmailEnabled(data.email_enabled_by_default);
            setFrequency(data.default_frequency);
            setEventTypes(data.event_types || []);
            
            if (data.quiet_hours_start && data.quiet_hours_end) {
                setQuietHoursStart(normalizeTime(data.quiet_hours_start));
                setQuietHoursEnd(normalizeTime(data.quiet_hours_end));
                setUseQuietHours(true);
            } else {
                setQuietHoursStart('22:00');
                setQuietHoursEnd('07:00');
                setUseQuietHours(false);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load notification defaults';
            setError(message);
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEventTypeToggle = (eventType: string) => {
        setEventTypes(prev => {
            if (prev.includes(eventType)) {
                return prev.filter(t => t !== eventType);
            } else {
                return [...prev, eventType];
            }
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validation
        if (eventTypes.length === 0) {
            toast.error('Please select at least one event type');
            return;
        }

        if (useQuietHours) {
            // Check if times are provided
            if (!quietHoursStart || !quietHoursEnd) {
                toast.error('Please specify both quiet hours start and end times');
                return;
            }
            
            // Validate time format (HH:MM) - HTML5 time input should always provide this format
            const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
            
            if (!timeRegex.test(quietHoursStart)) {
                console.error('Invalid quietHoursStart format:', quietHoursStart);
                toast.error(`Invalid start time format: "${quietHoursStart}". Expected HH:MM (e.g., 22:00)`);
                return;
            }
            
            if (!timeRegex.test(quietHoursEnd)) {
                console.error('Invalid quietHoursEnd format:', quietHoursEnd);
                toast.error(`Invalid end time format: "${quietHoursEnd}". Expected HH:MM (e.g., 07:00)`);
                return;
            }

            console.log('Submitting quiet hours - Start:', quietHoursStart, 'End:', quietHoursEnd);
        }

        setIsSaving(true);
        try {
            const updates: NotificationDefaultsUpdate = {
                email_enabled_by_default: emailEnabled,
                default_frequency: frequency,
                event_types: eventTypes,
                quiet_hours_start: useQuietHours ? quietHoursStart : null,
                quiet_hours_end: useQuietHours ? quietHoursEnd : null
            };

            console.log('Sending notification defaults update:', updates);
            const updated = await updateNotificationDefaults(updates);
            setDefaults(updated);
            toast.success('Notification defaults updated successfully');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update notification defaults';
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        if (defaults) {
            setEmailEnabled(defaults.email_enabled_by_default);
            setFrequency(defaults.default_frequency);
            setEventTypes(defaults.event_types || []);
            
            if (defaults.quiet_hours_start && defaults.quiet_hours_end) {
                setQuietHoursStart(defaults.quiet_hours_start);
                setQuietHoursEnd(defaults.quiet_hours_end);
                setUseQuietHours(true);
            } else {
                setUseQuietHours(false);
            }
            
            toast.success('Form reset to current defaults');
        }
    };

    if (isLoading) {
        return (
            <div className="loading-container min-h-screen">
                <div className="spinner"></div>
            </div>
        );
    }

    if (error && !defaults) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Card variant="elevated">
                    <Card.Body>
                        <div className="text-center py-12">
                            <p className="text-error text-lg mb-4">{error}</p>
                            <Button variant="primary" onClick={loadDefaults}>
                                Retry
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <Bell className="w-8 h-8 text-secondary-600 dark:text-secondary-400" />
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Notification Defaults
                    </h1>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                    Configure default notification preferences for new users
                </p>
            </div>

            {/* Settings Form */}
            <form onSubmit={handleSubmit}>
                {/* Email Settings */}
                <Card variant="elevated" className="mb-6">
                    <Card.Header
                        title="Email Notifications"
                        subtitle="Default email notification settings for new users"
                    />
                    <Card.Body>
                        <div className="space-y-4">
                            {/* Email Enabled Toggle */}
                            <div className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    id="email_enabled"
                                    checked={emailEnabled}
                                    onChange={(e) => setEmailEnabled(e.target.checked)}
                                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <div className="flex-1">
                                    <label htmlFor="email_enabled" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Enable email notifications by default
                                    </label>
                                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                        New users will have email notifications enabled when they register
                                    </p>
                                </div>
                            </div>

                            {/* Notification Frequency */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Default Notification Frequency
                                </label>
                                <div className="space-y-2">
                                    <div className="flex items-start gap-3">
                                        <input
                                            type="radio"
                                            id="freq_immediate"
                                            name="frequency"
                                            value="immediate"
                                            checked={frequency === 'immediate'}
                                            onChange={(e) => setFrequency(e.target.value as 'immediate' | 'daily' | 'weekly')}
                                            className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                        />
                                        <div className="flex-1">
                                            <label htmlFor="freq_immediate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Immediate
                                            </label>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                Send notifications as events occur (real-time)
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <input
                                            type="radio"
                                            id="freq_daily"
                                            name="frequency"
                                            value="daily"
                                            checked={frequency === 'daily'}
                                            onChange={(e) => setFrequency(e.target.value as 'immediate' | 'daily' | 'weekly')}
                                            className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                        />
                                        <div className="flex-1">
                                            <label htmlFor="freq_daily" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Daily Digest
                                            </label>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                Bundle notifications and send once per day (9:00 AM)
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <input
                                            type="radio"
                                            id="freq_weekly"
                                            name="frequency"
                                            value="weekly"
                                            checked={frequency === 'weekly'}
                                            onChange={(e) => setFrequency(e.target.value as 'immediate' | 'daily' | 'weekly')}
                                            className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                        />
                                        <div className="flex-1">
                                            <label htmlFor="freq_weekly" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Weekly Summary
                                            </label>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                Send weekly summary every Monday morning
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {/* Event Types */}
                <Card variant="elevated" className="mb-6">
                    <Card.Header
                        title="Event Types"
                        subtitle="Select which events trigger notifications"
                    />
                    <Card.Body>
                        <div className="space-y-3">
                            {availableEventTypes.map((event) => (
                                <div key={event.value} className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        id={`event_${event.value}`}
                                        checked={eventTypes.includes(event.value)}
                                        onChange={() => handleEventTypeToggle(event.value)}
                                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                    />
                                    <div className="flex-1">
                                        <label htmlFor={`event_${event.value}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            {event.label}
                                        </label>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {event.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {eventTypes.length === 0 && (
                            <p className="mt-4 text-sm text-warning">
                                ⚠️ At least one event type must be selected
                            </p>
                        )}
                    </Card.Body>
                </Card>

                {/* Quiet Hours */}
                <Card variant="elevated" className="mb-6">
                    <Card.Header
                        title="Quiet Hours"
                        subtitle="Prevent notifications during specified time range"
                    />
                    <Card.Body>
                        <div className="space-y-4">
                            {/* Use Quiet Hours Toggle */}
                            <div className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    id="use_quiet_hours"
                                    checked={useQuietHours}
                                    onChange={(e) => setUseQuietHours(e.target.checked)}
                                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <div className="flex-1">
                                    <label htmlFor="use_quiet_hours" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Enable quiet hours
                                    </label>
                                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                        No notifications will be sent during the specified time range
                                    </p>
                                </div>
                            </div>

                            {useQuietHours && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
                                    <QuietHoursTimePicker
                                        label="Start Time"
                                        value={quietHoursStart}
                                        onChange={setQuietHoursStart}
                                        disabled={!useQuietHours}
                                    />
                                    <QuietHoursTimePicker
                                        label="End Time"
                                        value={quietHoursEnd}
                                        onChange={setQuietHoursEnd}
                                        disabled={!useQuietHours}
                                    />
                                </div>
                            )}
                        </div>
                    </Card.Body>
                </Card>

                {/* Form Actions */}
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleReset}
                        disabled={isSaving}
                    >
                        Reset
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isSaving || eventTypes.length === 0}
                        isLoading={isSaving}
                        leftIcon={Save}
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </form>

            {/* Last Updated Info */}
            {defaults && (
                <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    Last updated: {new Date(defaults.updated_at).toLocaleString()}
                </div>
            )}
        </div>
    );
};

export default NotificationAdminPage;
